/* eslint-disable no-tabs */
/**
 * Copyright (c) 2021 Smartlife Labs
 * Size Selector Modal
 *
 * @project : smartlife-pro2-web
 * @createdDate : Tue Nov 30 2021
 * @author : Mayantha Jayawardena
 * -----
 * @lastModified : Tue Nov 30 2021
 * @modifiedBy : Mayantha Jayawardena
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import React, { useEffect, useRef, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { FORM_DEBOUNCE_TIME } from '../../../../../config/deviceConfig/Values';

import { IDFField } from '../../../../../config/deviceConfig/types';
import { ITEM_DEFAULTS } from '../../../../../config/deviceConfig/item_defaults';
import { IFormResetRef, FormResetAction } from '../../../ProDeviceCreateEdit/types';
import { APP_TEXT } from '../../../../../utility/AppTexts/DefaultAppText.utils';

const FIELD_NAME = ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.zs_modal_selector;
type ComponentProps = {
  fieldConfig: IDFField;
  validateForm: number;
  deviceInfo: any;
  resetForm: IFormResetRef;
  onChange: (payload: any, isInitializing?: boolean) => void;
};
interface IformData {
  modal: string;
}
// eslint-disable-next-line import/prefer-default-export
export const ProDeviceFieldSizeModalSelector: React.FunctionComponent<ComponentProps> = (props) => {
  const [deb, setDeb] = useState<BehaviorSubject<{values: IformData; errors: any} | null>>();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onSubmitForm = (formData: IformData) => {};

  const sendDefaults = () => {
    if (!props.fieldConfig || !deb) {
      return;
    }
    deb.next({
      values: {
        modal: props.fieldConfig.default,
      },
      errors: {},
    });
  };

  /**
   * Resetting or changing form data from parent
   */
  const refFormReset = useRef<IFormResetRef>({
    ref: 0,
    action: '',
    info: {},
  });
  useEffect(() => {
    if (
      formRef.current
      && props.resetForm.ref > 0
      && props.resetForm.ref > refFormReset.current.ref
    ) {
      refFormReset.current = props.resetForm;
      if (props.resetForm.action === FormResetAction.toCurrentInfo) {
        const values = props.resetForm.info.settings[props.fieldConfig.field];
        formRef.current.setValues({
          modal: values,
        });
      } else {
        formRef.current.resetForm();
      }
    }
  }, [props.resetForm]);
  const initilized = useRef(0);
  useEffect(() => {
    const b = new BehaviorSubject<{values: IformData; errors: any} | null>(null);
    setDeb(b);
    const sub = b
      .pipe(
        debounceTime(FORM_DEBOUNCE_TIME),
        map((v) => JSON.stringify(v)),
        distinctUntilChanged(),
        map((v) => JSON.parse(v)),
      )
      .subscribe((v: {values: IformData; errors: any} | null) => {
        if (v == null || props.fieldConfig == null) {
          return;
        }

        const { values, errors } = v;
        const infoData: any = {
          settings: {},
        };
        infoData.settings[props.fieldConfig.field] = values.modal;

        if (initilized.current === 0) {
          props.onChange(
            {
              action: 'data',
              form: FIELD_NAME,
              data: infoData,
              errors,
            },
            true,
          );
          initilized.current = 1;
        } else {
          props.onChange({
            action: 'data',
            form: FIELD_NAME,
            data: infoData,
            errors,
          });
        }
      });

    // send once default data
    sendDefaults();
    return () => {
      sub.unsubscribe();
    };
  }, [props.fieldConfig]);
  const formRef = useRef<any>();
  useEffect(() => {
    if (formRef && formRef.current !== undefined && props.validateForm !== 0) {
      formRef.current.submitForm();
    }
  }, [props.validateForm]);

  let isEditable = props.fieldConfig != null && props.fieldConfig.editable === true;
  if (isEditable) {
    if (props.deviceInfo.settings[`has_${props.fieldConfig.field}`] === false) {
      isEditable = false;
    }
  }

  return isEditable === true ? (
    <div>
      <Formik
        initialValues={{
          modal: props.fieldConfig?.default,
        }}
        innerRef={formRef}
        validationSchema={Yup.object().shape({
          modal: Yup.string().required('Required'),
        })}
        validateOnChange
        onSubmit={onSubmitForm}
      >
        {({
          errors, handleBlur, handleChange, touched, values,
        }) => {
          deb?.next({
            values,
            errors,
          });
          return (
            <form>
              <div className="text-fields-flex">
                <div>
                  <FormControl size="small" variant="outlined" className="dynamic-ui-form-control">
                    <InputLabel id="modal-label1" error={Boolean(touched.modal && errors.modal)}>
                      {APP_TEXT.device.tcp.TEXT1}
                      :
                    </InputLabel>
                    <Select
                      labelId="pin-label1"
                      value={values.modal}
                      name="modal"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      label={props.fieldConfig?.labels}
                    >
                      {ITEM_DEFAULTS.ALARM_SIZES.map((item: any) => (
                        <MenuItem value={item.value} key={JSON.stringify(item)}>
                          {item.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  ) : (
    <div />
  );
};
